import React from "react";
import { twMerge } from "tailwind-merge";
import { Heading } from "../Heading";

/**
 * Section Component — Semantic content grouping for dashboards
 *
 * A lightweight container that provides structure and hierarchy to page content.
 * Less heavy than a Card, more structured than a div. Perfect for organizing
 * related content with consistent spacing and optional headers.
 *
 * Usage:
 * ```tsx
 * <Section
 *   title="Overview"
 *   description="Key metrics and performance indicators"
 *   actions={<Button variant="ghost" size="sm">Refresh</Button>}
 * >
 *   <Grid cols={{ base: 1, lg: 3 }}>
 *     <Card>...</Card>
 *   </Grid>
 * </Section>
 * ```
 */

// ——————————————————————————————————————————
// Types
// ——————————————————————————————————————————

export type SectionVariant = "plain" | "contained" | "bordered";
export type SectionSpacing = "none" | "sm" | "md" | "lg";

export interface SectionProps {
  /** Section ID for linking/navigation (e.g., for use with SectionNav) */
  id?: string;
  /** Section title */
  title?: React.ReactNode;
  /** Optional description/subtitle */
  description?: React.ReactNode;
  /** Actions slot (buttons, links, etc.) */
  actions?: React.ReactNode;
  /** Visual variant */
  variant?: SectionVariant;
  /** Vertical spacing around section */
  spacing?: SectionSpacing;
  /** Show divider under header */
  withDivider?: boolean;
  /** Heading level for accessibility */
  headingAs?: "h2" | "h3" | "h4";
  /** Additional CSS classes */
  className?: string;
  /** Section content */
  children?: React.ReactNode;
  /** Label used by in-page navigation (PageLayout.Content with withSectionNav). Falls back to title when omitted */
  navLabel?: string;
}

// ——————————————————————————————————————————
// Styles
// ——————————————————————————————————————————

const variantStyles: Record<SectionVariant, string> = {
  plain: "",
  contained: "bg-background-surface rounded-lg border border-border-default p-6",
  bordered: "border-y border-border-default py-6",
};

const spacingStyles: Record<SectionSpacing, string> = {
  none: "",
  sm: "my-4",
  md: "my-6 md:my-8",
  lg: "my-8 md:my-12",
};

// ——————————————————————————————————————————
// Component
// ——————————————————————————————————————————

export function Section({
  id,
  title,
  description,
  actions,
  variant = "plain",
  spacing = "none",
  withDivider = false,
  headingAs = "h2",
  className,
  children,
}: SectionProps) {
  // Generate unique ID for accessibility
  const generatedHeadingId = React.useId();
  const headingId = id ? `${id}-heading` : generatedHeadingId;
  const hasHeader = title || description || actions;

  return (
    <section
      id={id}
      className={twMerge(spacingStyles[spacing], variantStyles[variant], className)}
      aria-labelledby={title ? headingId : undefined}
    >
      {hasHeader && (
        <header
          id={headingId}
          className={twMerge(
            "flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4",
            withDivider && "border-b border-border-default pb-4 mb-6",
            !withDivider && children && "mb-6"
          )}
        >
          <div className="min-w-0 flex-1">
            {title && (
              <Heading tag={headingAs} size="md" className="text-text-heading">
                {typeof title === "string" ? title : title}
              </Heading>
            )}
            {description && <p className="mt-2 text-base text-text-muted">{description}</p>}
          </div>
          {actions && <div className="flex-shrink-0">{actions}</div>}
        </header>
      )}

      {children && <div className={variant === "contained" ? "" : ""}>{children}</div>}
    </section>
  );
}

Section.displayName = "Section";
